'use client';

import { useMemo, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Select } from '@/components/atoms/select';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { EMPLOYEE_ROLES } from '@/consts/enums';

type ApiEmployee = ApiSchemas['EmployeeRead'];

export function CreateEmployeeDialog() {
  const [open, setOpen] = useState(false);
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const { mutate, isPending } = api.employees.createEmployeesPost.useMutation(
    {},
    {
      onSuccess: () => {
        toast.success('Employee created');
        api.employees.getAllEmployeesGet.invalidateQueries();
        setOpen(false);
      },
    }
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [role, setRole] = useState<ApiEmployee['role']>('STAFF');
  const [bookable, setBookable] = useState(false);

  const isDisabled = useMemo(() => {
    if (!email) return true;
    if (!firstName && !lastName) return true;
    if (isPending) return true;
    return false;
  }, [email, firstName, lastName, isPending]);

  const onSubmit = () => {
    if (!email) {
      toast.error('Email is required');
      return;
    }

    if (!firstName && !lastName) {
      toast.error('Either first name or last name is required');
      return;
    }

    mutate({
      office_id: officeId,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone_number: phoneNumber,
      role: role,
      status: 'ACTIVE',
      password: '',
      bookable: bookable,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size='sm'>
          <PlusIcon className='size-4' />
          Add Employee
        </Button>
      </DialogTrigger>
      <DialogContent showClose>
        <DialogHeader>
          <DialogTitle>Add Employee</DialogTitle>
          <DialogDescription>Add a new employee to your office.</DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='first_name'>First Name</Label>
            <Input
              id='first_name'
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className='col-span-3'
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='last_name'>Last Name</Label>
            <Input
              id='last_name'
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className='col-span-3'
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='email'>Email *</Label>
            <Input
              id='email'
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className='col-span-3'
              required
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='phone_number'>Phone Number</Label>
            <Input
              id='phone_number'
              type='tel'
              placeholder='123-456-7890'
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className='col-span-3'
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='role'>Role</Label>
            <Select
              options={EMPLOYEE_ROLES}
              value={role}
              onChange={(value) => setRole(value)}
              className='col-span-3 w-full'
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='bookable'>Bookable</Label>
            <Switch id='bookable' checked={bookable} onCheckedChange={(checked) => setBookable(checked)} />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button type='submit' onClick={onSubmit} disabled={isDisabled}>
            {isPending ? 'Creating...' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
